import type { CaseFilterOptions } from "@/cms/models";
import type { PropertyListSettings } from "./model";

export interface PropertyListTypeButton {
  namespace: string;
  displayName: string;
  /** cases under this type in the base list; automatic buttons only */
  count?: number;
}
export interface PropertyListTypeButtons {
  buttons: PropertyListTypeButton[];
  /** every type Core named, for the chip of a filter that has no button */
  names: Record<string, string>;
}

/**
 * Core's `filterOptions` returns per case its own estate type first and then its marketing types; Core's typeFilter
 * matches only the marketing types of a case that has them, so such a case counts under those alone. A case whose
 * marketing types repeat its own estate type, or all lack a coco name, is miscounted; that is accepted.
 */
export function typeFilterButtons(
  list: Pick<
    PropertyListSettings,
    | "typeFilterMode"
    | "typeFilters"
    | "hiddenTypes"
    | "typeFilterOrder"
    | "allFilterLabel"
  >,
  options: CaseFilterOptions[],
): PropertyListTypeButtons {
  const all = { namespace: "all", displayName: list.allFilterLabel };
  const names: Record<string, string> = {};
  for (const entry of options)
    for (const option of entry.filterOptions ?? [])
      names[option.namespace] ??= option.displayName;
  if (list.typeFilterMode === "fixed")
    return {
      buttons: list.typeFilters.length ? [all, ...list.typeFilters] : [],
      names,
    };
  if (list.typeFilterMode === "none") return { buttons: [], names };
  const counts = new Map<string, number>();
  for (const entry of options) {
    const own = entry.filterOptions ?? [];
    const counted = new Set(
      (own.length > 1 ? own.slice(1) : own).map((option) => option.namespace),
    );
    for (const namespace of counted)
      counts.set(namespace, (counts.get(namespace) ?? 0) + 1);
  }
  const types = [...counts]
    .filter(
      ([namespace, count]) =>
        count > 0 && !list.hiddenTypes.includes(namespace),
    )
    .map(([namespace, count]) => ({
      namespace,
      displayName: names[namespace] ?? namespace,
      count,
    }))
    .sort(
      (a, b) =>
        (list.typeFilterOrder === "count" ? b.count - a.count : 0) ||
        a.displayName.localeCompare(b.displayName, "da"),
    );
  return { buttons: types.length < 2 ? [] : [all, ...types], names };
}

/** The chip's name for a filter without a button: Core's name when it knows the type, else the namespace's last part. */
export function typeName(
  namespace: string,
  names: Record<string, string>,
): string {
  if (names[namespace]) return names[namespace];
  const last = namespace.split(".").pop() || namespace;
  return last.charAt(0).toUpperCase() + last.slice(1);
}
